function _typeof(o) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) { return typeof o; } : function (o) { return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o; }, _typeof(o); }
function _classCallCheck(a, n) { if (!(a instanceof n)) throw new TypeError("Cannot call a class as a function"); }
function _defineProperties(e, r) { for (var t = 0; t < r.length; t++) { var o = r[t]; o.enumerable = o.enumerable || !1, o.configurable = !0, "value" in o && (o.writable = !0), Object.defineProperty(e, _toPropertyKey(o.key), o); } }
function _createClass(e, r, t) { return r && _defineProperties(e.prototype, r), t && _defineProperties(e, t), Object.defineProperty(e, "prototype", { writable: !1 }), e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == _typeof(i) ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != _typeof(t) || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != _typeof(i)) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _callSuper(t, o, e) { return o = _getPrototypeOf(o), _possibleConstructorReturn(t, _isNativeReflectConstruct() ? Reflect.construct(o, e || [], _getPrototypeOf(t).constructor) : o.apply(t, e)); }
function _possibleConstructorReturn(t, e) { if (e && ("object" == _typeof(e) || "function" == typeof e)) return e; if (void 0 !== e) throw new TypeError("Derived constructors may only return object or undefined"); return _assertThisInitialized(t); }
function _assertThisInitialized(e) { if (void 0 === e) throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); return e; }
function _isNativeReflectConstruct() { try { var t = !Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); } catch (t) {} return (_isNativeReflectConstruct = function _isNativeReflectConstruct() { return !!t; })(); }
function _getPrototypeOf(t) { return _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf.bind() : function (t) { return t.__proto__ || Object.getPrototypeOf(t); }, _getPrototypeOf(t); }
function _inherits(t, e) { if ("function" != typeof e && null !== e) throw new TypeError("Super expression must either be null or a function"); t.prototype = Object.create(e && e.prototype, { constructor: { value: t, writable: !0, configurable: !0 } }), Object.defineProperty(t, "prototype", { writable: !1 }), e && _setPrototypeOf(t, e); }
function _setPrototypeOf(t, e) { return _setPrototypeOf = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function (t, e) { return t.__proto__ = e, t; }, _setPrototypeOf(t, e); }
/**
 * @module ol/format/GeoJSON
 */

import Feature from '../Feature.js';
import JSONFeature from './JSONFeature.js';
import RenderFeature from '../render/Feature.js';
import { createGeometry, createRenderFeature, transformGeometryWithOptions } from './Feature.js';
import { deflateCoordinatesArray, deflateMultiCoordinatesArray } from '../geom/flat/deflate.js';
import { getLayoutForStride } from '../geom/SimpleGeometry.js';
import { get as getProjection } from '../proj.js';
import { isEmpty } from '../obj.js';

/**
 * @typedef {import("geojson").GeoJSON} GeoJSONObject
 * @typedef {import("geojson").Feature} GeoJSONFeature
 * @typedef {import("geojson").FeatureCollection} GeoJSONFeatureCollection
 * @typedef {import("geojson").Geometry} GeoJSONGeometry
 * @typedef {import("geojson").Point} GeoJSONPoint
 * @typedef {import("geojson").LineString} GeoJSONLineString
 * @typedef {import("geojson").Polygon} GeoJSONPolygon
 * @typedef {import("geojson").MultiPoint} GeoJSONMultiPoint
 * @typedef {import("geojson").MultiLineString} GeoJSONMultiLineString
 * @typedef {import("geojson").MultiPolygon} GeoJSONMultiPolygon
 * @typedef {import("geojson").GeometryCollection} GeoJSONGeometryCollection
 */

/**
 * @template {import("../Feature.js").FeatureClass} FeatureClassToFeature
 * @typedef {Object} Options
 *
 * @property {import("../proj.js").ProjectionLike} [dataProjection='EPSG:4326'] Default data projection.
 * @property {import("../proj.js").ProjectionLike} [featureProjection] Projection for features read or
 * written by the format.  Options passed to read or write methods will take precedence.
 * @property {string} [geometryName] Geometry name to use when creating features.
 * @property {boolean} [extractGeometryName=false] Certain GeoJSON providers include
 * the geometry_name field in the feature GeoJSON. If set to `true` the GeoJSON reader
 * will look for that field to set the geometry name. If both this field is set to `true`
 * and a `geometryName` is provided, the `geometryName` will take precedence.
 * @property {FeatureClassToFeature} [featureClass] Feature class
 * to be used when reading features. The default is {@link module:ol/Feature~Feature}. If performance is
 * the primary concern, and features are not going to be modified or round-tripped through the format,
 * consider using {@link module:ol/render/Feature~RenderFeature}
 */

/**
 * @classdesc
 * Feature format for reading and writing data in the GeoJSON format.
 *
 * @template {import('../Feature.js').FeatureClass} [T=typeof Feature]
 * @extends {JSONFeature<T>}
 * @api
 */
var GeoJSON = /*#__PURE__*/function (_JSONFeature) {
  /**
   * @param {Options<T>} [options] Options.
   */
  function GeoJSON(options) {
    var _this;
    _classCallCheck(this, GeoJSON);
    options = options ? options : {};
    _this = _callSuper(this, GeoJSON);

    /**
     * @type {import("../proj/Projection.js").default}
     */
    _this.dataProjection = getProjection(options.dataProjection ? options.dataProjection : 'EPSG:4326');
    if (options.featureProjection) {
      /**
       * @type {import("../proj/Projection.js").default}
       */
      _this.defaultFeatureProjection = getProjection(options.featureProjection);
    }
    if (options.featureClass) {
      _this.featureClass = options.featureClass;
    }

    /**
     * Name of the geometry attribute for features.
     * @type {string|undefined}
     * @private
     */
    _this.geometryName_ = options.geometryName;

    /**
     * Look for the `geometry_name` in the feature GeoJSON
     * @type {boolean|undefined}
     * @private
     */
    _this.extractGeometryName_ = options.extractGeometryName;
    _this.supportedMediaTypes = ['application/geo+json', 'application/vnd.geo+json'];
    return _this;
  }

  /**
   * @param {Object} object Object.
   * @param {import("./Feature.js").ReadOptions} [options] Read options.
   * @protected
   * @return {Feature|RenderFeature|Array<RenderFeature>}.default} Feature.
   */
  _inherits(GeoJSON, _JSONFeature);
  return _createClass(GeoJSON, [{
    key: "readFeatureFromObject",
    value: function readFeatureFromObject(object, options) {
      /**
       * @type {GeoJSONFeature}
       */
      var geoJSONFeature = null;
      if (object['type'] === 'Feature') {
        geoJSONFeature = /** @type {GeoJSONFeature} */object;
      } else {
        geoJSONFeature = {
          'type': 'Feature',
          'geometry': (/** @type {GeoJSONGeometry} */object),
          'properties': null
        };
      }
      var geometry = readGeometryInternal(geoJSONFeature['geometry'], options);
      if (this.featureClass === RenderFeature) {
        return createRenderFeature({
          geometry: geometry,
          id: geoJSONFeature['id'],
          properties: geoJSONFeature['properties']
        }, options);
      }
      var feature = new Feature();
      if (this.geometryName_) {
        feature.setGeometryName(this.geometryName_);
      } else if (this.extractGeometryName_ && geoJSONFeature['geometry_name']) {
        feature.setGeometryName(geoJSONFeature['geometry_name']);
      }
      feature.setGeometry(createGeometry(geometry, options));
      if ('id' in geoJSONFeature) {
        feature.setId(geoJSONFeature['id']);
      }
      if (geoJSONFeature['properties']) {
        feature.setProperties(geoJSONFeature['properties'], true);
      }
      return feature;
    }

    /**
     * @param {Object} object Object.
     * @param {import("./Feature.js").ReadOptions} [options] Read options.
     * @protected
     * @return {Array<Feature|RenderFeature>} Features.
     */
  }, {
    key: "readFeaturesFromObject",
    value: function readFeaturesFromObject(object, options) {
      var geoJSONObject = /** @type {GeoJSONObject} */object;
      /** @type {Array<Feature|RenderFeature|Array<RenderFeature>>} */
      var features = null;
      if (geoJSONObject['type'] === 'FeatureCollection') {
        var geoJSONFeatureCollection = /** @type {GeoJSONFeatureCollection} */
        object;
        features = [];
        var geoJSONFeatures = geoJSONFeatureCollection['features'];
        for (var i = 0, ii = geoJSONFeatures.length; i < ii; ++i) {
          var featureObject = this.readFeatureFromObject(geoJSONFeatures[i], options);
          if (!featureObject) {
            continue;
          }
          features.push(featureObject);
        }
      } else {
        features = [this.readFeatureFromObject(object, options)];
      }
      return features.flat();
    }

    /**
     * @param {GeoJSONGeometry} object Object.
     * @param {import("./Feature.js").ReadOptions} [options] Read options.
     * @protected
     * @return {import("../geom/Geometry.js").default} Geometry.
     */
  }, {
    key: "readGeometryFromObject",
    value: function readGeometryFromObject(object, options) {
      return readGeometry(object, options);
    }

    /**
     * @param {Object} object Object.
     * @protected
     * @return {import("../proj/Projection.js").default} Projection.
     */
  }, {
    key: "readProjectionFromObject",
    value: function readProjectionFromObject(object) {
      var crs = object['crs'];
      var projection;
      if (crs) {
        if (crs['type'] == 'name') {
          projection = getProjection(crs['properties']['name']);
        } else if (crs['type'] === 'EPSG') {
          projection = getProjection('EPSG:' + crs['properties']['code']);
        } else {
          throw new Error('Unknown SRS type');
        }
      } else {
        projection = this.dataProjection;
      }
      return /** @type {import("../proj/Projection.js").default} */projection;
    }

    /**
     * Encode a feature as a GeoJSON Feature object.
     *
     * @param {import("../Feature.js").default} feature Feature.
     * @param {import("./Feature.js").WriteOptions} [options] Write options.
     * @return {GeoJSONFeature} Object.
     * @api
     */
  }, {
    key: "writeFeatureObject",
    value: function writeFeatureObject(feature, options) {
      options = this.adaptOptions(options);

      /** @type {GeoJSONFeature} */
      var object = {
        'type': 'Feature',
        geometry: null,
        properties: null
      };
      var id = feature.getId();
      if (id !== undefined) {
        object.id = id;
      }
      if (!feature.hasProperties()) {
        return object;
      }
      var properties = feature.getProperties();
      var geometry = feature.getGeometry();
      if (geometry) {
        object.geometry = writeGeometry(geometry, options);
        delete properties[feature.getGeometryName()];
      }
      if (!isEmpty(properties)) {
        object.properties = properties;
      }
      return object;
    }

    /**
     * Encode an array of features as a GeoJSON object.
     *
     * @param {Array<import("../Feature.js").default>} features Features.
     * @param {import("./Feature.js").WriteOptions} [options] Write options.
     * @return {GeoJSONFeatureCollection} GeoJSON Object.
     * @api
     */
  }, {
    key: "writeFeaturesObject",
    value: function writeFeaturesObject(features, options) {
      options = this.adaptOptions(options);
      var objects = [];
      for (var i = 0, ii = features.length; i < ii; ++i) {
        objects.push(this.writeFeatureObject(features[i], options));
      }
      return {
        type: 'FeatureCollection',
        features: objects
      };
    }

    /**
     * Encode a geometry as a GeoJSON object.
     *
     * @param {import("../geom/Geometry.js").default} geometry Geometry.
     * @param {import("./Feature.js").WriteOptions} [options] Write options.
     * @return {GeoJSONGeometry|GeoJSONGeometryCollection} Object.
     * @api
     */
  }, {
    key: "writeGeometryObject",
    value: function writeGeometryObject(geometry, options) {
      return writeGeometry(geometry, this.adaptOptions(options));
    }
  }]);
}(JSONFeature);
/**
 * @param {GeoJSONGeometry|GeoJSONGeometryCollection} object Object.
 * @param {import("./Feature.js").ReadOptions} [options] Read options.
 * @return {import("./Feature.js").GeometryObject} Geometry.
 */
function readGeometryInternal(object, options) {
  if (!object) {
    return null;
  }

  /** @type {import("./Feature.js").GeometryObject} */
  var geometry;
  switch (object['type']) {
    case 'Point':
      {
        geometry = readPointGeometry(/** @type {GeoJSONPoint} */object);
        break;
      }
    case 'LineString':
      {
        geometry = readLineStringGeometry(/** @type {GeoJSONLineString} */object);
        break;
      }
    case 'Polygon':
      {
        geometry = readPolygonGeometry(/** @type {GeoJSONPolygon} */object);
        break;
      }
    case 'MultiPoint':
      {
        geometry = readMultiPointGeometry(/** @type {GeoJSONMultiPoint} */object);
        break;
      }
    case 'MultiLineString':
      {
        geometry = readMultiLineStringGeometry(/** @type {GeoJSONMultiLineString} */object);
        break;
      }
    case 'MultiPolygon':
      {
        geometry = readMultiPolygonGeometry(/** @type {GeoJSONMultiPolygon} */object);
        break;
      }
    case 'GeometryCollection':
      {
        geometry = readGeometryCollectionGeometry(/** @type {GeoJSONGeometryCollection} */object);
        break;
      }
    default:
      {
        throw new Error('Unsupported GeoJSON type: ' + object['type']);
      }
  }
  return geometry;
}

/**
 * @param {GeoJSONGeometry|GeoJSONGeometryCollection} object Object.
 * @param {import("./Feature.js").ReadOptions} [options] Read options.
 * @return {import("../geom/Geometry.js").default} Geometry.
 */
function readGeometry(object, options) {
  var geometryObject = readGeometryInternal(object, options);
  return createGeometry(geometryObject, options);
}

/**
 * @param {GeoJSONGeometryCollection} object Object.
 * @param {import("./Feature.js").ReadOptions} [options] Read options.
 * @return {import("./Feature.js").GeometryCollectionObject} Geometry collection.
 */
function readGeometryCollectionGeometry(object, options) {
  var geometries = object['geometries'].map(
  /**
   * @param {GeoJSONGeometry} geometry Geometry.
   * @return {import("./Feature.js").GeometryObject} geometry Geometry.
   */
  function (geometry) {
    return readGeometryInternal(geometry, options);
  });
  return geometries;
}

/**
 * @param {GeoJSONPoint} object Input object.
 * @return {import("./Feature.js").GeometryObject} Point geometry.
 */
function readPointGeometry(object) {
  var flatCoordinates = object['coordinates'];
  return {
    type: 'Point',
    flatCoordinates: flatCoordinates,
    layout: getLayoutForStride(flatCoordinates.length)
  };
}

/**
 * @param {GeoJSONLineString} object Object.
 * @return {import("./Feature.js").GeometryObject} LineString geometry.
 */
function readLineStringGeometry(object) {
  var _coordinates$;
  var coordinates = object['coordinates'];
  var flatCoordinates = coordinates.flat();
  return {
    type: 'LineString',
    flatCoordinates: flatCoordinates,
    ends: [flatCoordinates.length],
    layout: getLayoutForStride(((_coordinates$ = coordinates[0]) === null || _coordinates$ === void 0 ? void 0 : _coordinates$.length) || 2)
  };
}

/**
 * @param {GeoJSONMultiLineString} object Object.
 * @return {import("./Feature.js").GeometryObject} MultiLineString geometry.
 */
function readMultiLineStringGeometry(object) {
  var _coordinates$2;
  var coordinates = object['coordinates'];
  var stride = ((_coordinates$2 = coordinates[0]) === null || _coordinates$2 === void 0 || (_coordinates$2 = _coordinates$2[0]) === null || _coordinates$2 === void 0 ? void 0 : _coordinates$2.length) || 2;
  var flatCoordinates = [];
  var ends = deflateCoordinatesArray(flatCoordinates, 0, coordinates, stride);
  return {
    type: 'MultiLineString',
    flatCoordinates: flatCoordinates,
    ends: ends,
    layout: getLayoutForStride(stride)
  };
}

/**
 * @param {GeoJSONMultiPoint} object Object.
 * @return {import("./Feature.js").GeometryObject} MultiPoint geometry.
 */
function readMultiPointGeometry(object) {
  var _coordinates$3;
  var coordinates = object['coordinates'];
  return {
    type: 'MultiPoint',
    flatCoordinates: coordinates.flat(),
    layout: getLayoutForStride(((_coordinates$3 = coordinates[0]) === null || _coordinates$3 === void 0 ? void 0 : _coordinates$3.length) || 2)
  };
}

/**
 * @param {GeoJSONMultiPolygon} object Object.
 * @return {import("./Feature.js").GeometryObject} MultiPolygon geometry.
 */
function readMultiPolygonGeometry(object) {
  var _coordinates$4;
  var coordinates = object['coordinates'];
  var flatCoordinates = [];
  var stride = ((_coordinates$4 = coordinates[0]) === null || _coordinates$4 === void 0 || (_coordinates$4 = _coordinates$4[0]) === null || _coordinates$4 === void 0 ? void 0 : _coordinates$4[0].length) || 2;
  var endss = deflateMultiCoordinatesArray(flatCoordinates, 0, coordinates, stride);
  return {
    type: 'MultiPolygon',
    flatCoordinates: flatCoordinates,
    ends: endss,
    layout: getLayoutForStride(stride)
  };
}

/**
 * @param {GeoJSONPolygon} object Object.
 * @return {import("./Feature.js").GeometryObject} Polygon.
 */
function readPolygonGeometry(object) {
  var _coordinates$5;
  var coordinates = object['coordinates'];
  var flatCoordinates = [];
  var stride = (_coordinates$5 = coordinates[0]) === null || _coordinates$5 === void 0 || (_coordinates$5 = _coordinates$5[0]) === null || _coordinates$5 === void 0 ? void 0 : _coordinates$5.length;
  var ends = deflateCoordinatesArray(flatCoordinates, 0, coordinates, stride);
  return {
    type: 'Polygon',
    flatCoordinates: flatCoordinates,
    ends: ends,
    layout: getLayoutForStride(stride)
  };
}

/**
 * @param {import("../geom/Geometry.js").default} geometry Geometry.
 * @param {import("./Feature.js").WriteOptions} [options] Write options.
 * @return {GeoJSONGeometry} GeoJSON geometry.
 */
function writeGeometry(geometry, options) {
  geometry = transformGeometryWithOptions(geometry, true, options);
  var type = geometry.getType();

  /** @type {GeoJSONGeometry} */
  var geoJSON;
  switch (type) {
    case 'Point':
      {
        geoJSON = writePointGeometry(/** @type {import("../geom/Point.js").default} */geometry, options);
        break;
      }
    case 'LineString':
      {
        geoJSON = writeLineStringGeometry(/** @type {import("../geom/LineString.js").default} */geometry, options);
        break;
      }
    case 'Polygon':
      {
        geoJSON = writePolygonGeometry(/** @type {import("../geom/Polygon.js").default} */geometry, options);
        break;
      }
    case 'MultiPoint':
      {
        geoJSON = writeMultiPointGeometry(/** @type {import("../geom/MultiPoint.js").default} */geometry, options);
        break;
      }
    case 'MultiLineString':
      {
        geoJSON = writeMultiLineStringGeometry(/** @type {import("../geom/MultiLineString.js").default} */geometry, options);
        break;
      }
    case 'MultiPolygon':
      {
        geoJSON = writeMultiPolygonGeometry(/** @type {import("../geom/MultiPolygon.js").default} */geometry, options);
        break;
      }
    case 'GeometryCollection':
      {
        geoJSON = writeGeometryCollectionGeometry(/** @type {import("../geom/GeometryCollection.js").default} */
        geometry, options);
        break;
      }
    case 'Circle':
      {
        geoJSON = {
          type: 'GeometryCollection',
          geometries: []
        };
        break;
      }
    default:
      {
        throw new Error('Unsupported geometry type: ' + type);
      }
  }
  return geoJSON;
}

/**
 * @param {import("../geom/GeometryCollection.js").default} geometry Geometry.
 * @param {import("./Feature.js").WriteOptions} [options] Write options.
 * @return {GeoJSONGeometryCollection} GeoJSON geometry collection.
 */
function writeGeometryCollectionGeometry(geometry, options) {
  options = Object.assign({}, options);
  delete options.featureProjection;
  var geometries = geometry.getGeometriesArray().map(function (geometry) {
    return writeGeometry(geometry, options);
  });
  return {
    type: 'GeometryCollection',
    geometries: geometries
  };
}

/**
 * @param {import("../geom/LineString.js").default} geometry Geometry.
 * @param {import("./Feature.js").WriteOptions} [options] Write options.
 * @return {GeoJSONGeometry} GeoJSON geometry.
 */
function writeLineStringGeometry(geometry, options) {
  return {
    type: 'LineString',
    coordinates: geometry.getCoordinates()
  };
}

/**
 * @param {import("../geom/MultiLineString.js").default} geometry Geometry.
 * @param {import("./Feature.js").WriteOptions} [options] Write options.
 * @return {GeoJSONGeometry} GeoJSON geometry.
 */
function writeMultiLineStringGeometry(geometry, options) {
  return {
    type: 'MultiLineString',
    coordinates: geometry.getCoordinates()
  };
}

/**
 * @param {import("../geom/MultiPoint.js").default} geometry Geometry.
 * @param {import("./Feature.js").WriteOptions} [options] Write options.
 * @return {GeoJSONGeometry} GeoJSON geometry.
 */
function writeMultiPointGeometry(geometry, options) {
  return {
    type: 'MultiPoint',
    coordinates: geometry.getCoordinates()
  };
}

/**
 * @param {import("../geom/MultiPolygon.js").default} geometry Geometry.
 * @param {import("./Feature.js").WriteOptions} [options] Write options.
 * @return {GeoJSONGeometry} GeoJSON geometry.
 */
function writeMultiPolygonGeometry(geometry, options) {
  var right;
  if (options) {
    right = options.rightHanded;
  }
  return {
    type: 'MultiPolygon',
    coordinates: geometry.getCoordinates(right)
  };
}

/**
 * @param {import("../geom/Point.js").default} geometry Geometry.
 * @param {import("./Feature.js").WriteOptions} [options] Write options.
 * @return {GeoJSONGeometry} GeoJSON geometry.
 */
function writePointGeometry(geometry, options) {
  return {
    type: 'Point',
    coordinates: geometry.getCoordinates()
  };
}

/**
 * @param {import("../geom/Polygon.js").default} geometry Geometry.
 * @param {import("./Feature.js").WriteOptions} [options] Write options.
 * @return {GeoJSONGeometry} GeoJSON geometry.
 */
function writePolygonGeometry(geometry, options) {
  var right;
  if (options) {
    right = options.rightHanded;
  }
  return {
    type: 'Polygon',
    coordinates: geometry.getCoordinates(right)
  };
}
export default GeoJSON;